import { getFromDataPath, mutateByPath, removeByPath } from "../../../utility";
import { ArsTableCellType, ArsTableState, ArstActionContextmenuAction } from "../interface";
import { isArsTableSubGroup } from "../typeguards/isArsTableSubGroup";
import { arst_checkRowHeaders } from "./arst_checkRowHeaders";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";

/**
 * Toggles the hidden flag for a row or column group label target.
 *
 * @param state - Current ARS table state
 * @param action - Context-menu action describing the group label cell to toggle
 * @returns Updated ARS table state
 */
export const arst_toggleGroupLabelVisibility = <T>(
  state: ArsTableState<T>,
  action: ArstActionContextmenuAction<T>,
): ArsTableState<T> => {
  if (
    action.cellValue.cellType !== ArsTableCellType.ColumnGroupCell &&
    action.cellValue.cellType !== ArsTableCellType.ColumnGroupLabel &&
    action.cellValue.cellType !== ArsTableCellType.RowGroupCell &&
    action.cellValue.cellType !== ArsTableCellType.RowGroupLabel
  ) {
    return state;
  }

  const targetGroupingFactorPath = arst_resolveTargetPath(action.cellValue.dataPath);
  const targetItem = getFromDataPath(targetGroupingFactorPath, state.bodyData, (obj) =>
    isArsTableSubGroup(obj, state.settings.editorProps.isT),
  );
  if (!targetItem) return state;

  const isRowGroupCell =
    action.cellValue.cellType === ArsTableCellType.RowGroupCell ||
    action.cellValue.cellType === ArsTableCellType.RowGroupLabel;

  let newBodyData = { ...state.bodyData };
  /* istanbul ignore else */
  if (targetItem?.hidden) {
    // Remove if it is already there
    newBodyData = removeByPath(newBodyData, `${targetGroupingFactorPath}/hidden`);
    if (isRowGroupCell) {
      // Add rowHeader cell back in, columnIndex here refers to its position within rowHeaders
      newBodyData.rowHeaders = [...state.bodyData.rowHeaders];
      newBodyData.rowHeaders.splice(action.cellValue.columnIndex, 0, {
        heights: [],
        label: state.settings.editorProps.blankT,
        width: 120,
      });
    } else {
      newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
    }
  } else if (targetItem) {
    // Add if it is not there or false
    newBodyData = mutateByPath(state.bodyData, `${targetGroupingFactorPath}/hidden`, true);
    newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
  }

  return {
    ...state,
    bodyData: newBodyData,
    changed: true,
  };
};
